export type ProviderRank = "Apprentice" | "Skilled" | "Professional" | "Master";
export type PricingModel = "hourly" | "fixed" | "inspection";
export type AvailabilityStatus = "online" | "offline" | "busy";

export interface Review {
  id: string;
  userName: string;
  userAvatar: string;
  rating: number;
  comment: string;
  date: string;
}

export interface ServiceProvider {
  id: string;
  name: string;
  businessName?: string;
  avatar: string;
  coverImage: string;
  designation: string;
  rank: ProviderRank;
  yearsOfExperience: number;
  categoryId: string;
  subCategoryId: string;
  services: string[];
  pricingModel: PricingModel;
  priceRange: { min: number; max: number };
  availability: AvailabilityStatus;
  rating: number;
  reviewCount: number;
  completedJobs: number;
  location: {
    state: string;
    lga: string;
    area: string;
  };
  verified: {
    id: boolean;
    phone: boolean;
    certification: boolean;
  };
  bio: string;
  joinedDate: string;
  responseTime: string;
  reviews: Review[];
}

export const sampleProviders: ServiceProvider[] = [
  // Construction & Repairs
  {
    id: "p1",
    name: "Chukwudi Okafor",
    businessName: "Chudi Plumbing Services",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=400&fit=crop",
    designation: "Master Plumber",
    rank: "Master",
    yearsOfExperience: 15,
    categoryId: "construction",
    subCategoryId: "plumbing",
    services: ["Pipe Installation", "Leak Repairs", "Bathroom Fitting", "Water Heater Installation", "Drainage Systems"],
    pricingModel: "inspection",
    priceRange: { min: 5000, max: 50000 },
    availability: "online",
    rating: 4.9,
    reviewCount: 234,
    completedJobs: 450,
    location: { state: "Lagos State", lga: "Ikeja", area: "Opebi" },
    verified: { id: true, phone: true, certification: true },
    bio: "With over 15 years of experience in plumbing, I provide top-quality services for residential and commercial properties. Certified by NABTEB and committed to excellence.",
    joinedDate: "2019-03-15",
    responseTime: "Within 1 hour",
    reviews: [
      { id: "r1", userName: "Adaeze N.", userAvatar: "https://i.pravatar.cc/40?img=1", rating: 5, comment: "Excellent work! Fixed my bathroom issues in no time.", date: "2024-01-10" },
      { id: "r2", userName: "Tunde A.", userAvatar: "https://i.pravatar.cc/40?img=2", rating: 5, comment: "Very professional and affordable. Highly recommend!", date: "2024-01-05" },
    ],
  },
  {
    id: "p2",
    name: "Emmanuel Adeyemi",
    businessName: "Sparks Electrical",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&h=400&fit=crop",
    designation: "Certified Electrician",
    rank: "Professional",
    yearsOfExperience: 10,
    categoryId: "construction",
    subCategoryId: "electrical",
    services: ["Wiring Installation", "Panel Upgrades", "Lighting Installation", "Generator Maintenance", "Fault Detection"],
    pricingModel: "hourly",
    priceRange: { min: 3000, max: 15000 },
    availability: "online",
    rating: 4.8,
    reviewCount: 189,
    completedJobs: 320,
    location: { state: "Lagos State", lga: "Eti-Osa", area: "Lekki Phase 1" },
    verified: { id: true, phone: true, certification: true },
    bio: "Professional electrician specializing in residential and commercial electrical systems. Safety-focused and detail-oriented.",
    joinedDate: "2020-06-20",
    responseTime: "Within 30 minutes",
    reviews: [],
  },
  {
    id: "p3",
    name: "Blessing Nwachukwu",
    avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&h=400&fit=crop",
    designation: "Interior Painter",
    rank: "Skilled",
    yearsOfExperience: 7,
    categoryId: "construction",
    subCategoryId: "painting",
    services: ["Interior Painting", "Exterior Painting", "Wallpaper Installation", "Texture Finishes", "Color Consultation"],
    pricingModel: "fixed",
    priceRange: { min: 20000, max: 150000 },
    availability: "busy",
    rating: 4.7,
    reviewCount: 156,
    completedJobs: 280,
    location: { state: "FCT Abuja", lga: "Abuja Municipal", area: "Wuse 2" },
    verified: { id: true, phone: true, certification: false },
    bio: "Transforming spaces with beautiful colors and finishes. Specializing in modern interior designs with attention to detail.",
    joinedDate: "2021-01-10",
    responseTime: "Within 2 hours",
    reviews: [],
  },
  // Automotive
  {
    id: "p4",
    name: "Ibrahim Musa",
    businessName: "Ibrahim Auto Repairs",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&h=400&fit=crop",
    designation: "Auto Mechanic",
    rank: "Master",
    yearsOfExperience: 20,
    categoryId: "automotive",
    subCategoryId: "mechanic",
    services: ["Engine Repair", "Brake Service", "Transmission", "Suspension", "Diagnostics"],
    pricingModel: "inspection",
    priceRange: { min: 5000, max: 200000 },
    availability: "online",
    rating: 4.9,
    reviewCount: 412,
    completedJobs: 850,
    location: { state: "Lagos State", lga: "Surulere", area: "Ojuelegba" },
    verified: { id: true, phone: true, certification: true },
    bio: "Master mechanic with 20 years experience. Specializing in all vehicle makes, especially Toyota, Honda, and Mercedes.",
    joinedDate: "2018-05-01",
    responseTime: "Within 15 minutes",
    reviews: [],
  },
  // ICT & Digital
  {
    id: "p5",
    name: "Oluwaseun Bakare",
    businessName: "TechGenius Solutions",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&h=400&fit=crop",
    designation: "Full Stack Developer",
    rank: "Professional",
    yearsOfExperience: 8,
    categoryId: "ict",
    subCategoryId: "web-dev",
    services: ["Website Development", "E-commerce Sites", "Web Applications", "API Development", "Maintenance"],
    pricingModel: "fixed",
    priceRange: { min: 100000, max: 2000000 },
    availability: "online",
    rating: 4.8,
    reviewCount: 78,
    completedJobs: 95,
    location: { state: "Lagos State", lga: "Yaba", area: "Yaba" },
    verified: { id: true, phone: true, certification: true },
    bio: "Building modern web solutions for businesses. Expert in React, Node.js, and cloud technologies. Let's bring your ideas to life!",
    joinedDate: "2020-09-15",
    responseTime: "Within 1 hour",
    reviews: [],
  },
  {
    id: "p6",
    name: "Chidinma Eze",
    businessName: "CreativePixel Design",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&h=400&fit=crop",
    designation: "Graphics Designer",
    rank: "Professional",
    yearsOfExperience: 6,
    categoryId: "ict",
    subCategoryId: "graphics",
    services: ["Logo Design", "Brand Identity", "Social Media Graphics", "Flyers & Posters", "UI/UX Design"],
    pricingModel: "fixed",
    priceRange: { min: 15000, max: 300000 },
    availability: "online",
    rating: 4.9,
    reviewCount: 203,
    completedJobs: 380,
    location: { state: "Rivers State", lga: "Port Harcourt", area: "GRA Phase 2" },
    verified: { id: true, phone: true, certification: false },
    bio: "Creative designer passionate about helping brands stand out. From logos to complete brand identities, I've got you covered!",
    joinedDate: "2021-02-20",
    responseTime: "Within 2 hours",
    reviews: [],
  },
  // Creative & Media
  {
    id: "p7",
    name: "Afolabi Johnson",
    businessName: "AJ Studios",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&h=400&fit=crop",
    designation: "Professional Photographer",
    rank: "Master",
    yearsOfExperience: 12,
    categoryId: "creative",
    subCategoryId: "photography",
    services: ["Wedding Photography", "Event Coverage", "Portrait Sessions", "Product Photography", "Corporate Events"],
    pricingModel: "fixed",
    priceRange: { min: 50000, max: 500000 },
    availability: "busy",
    rating: 4.9,
    reviewCount: 345,
    completedJobs: 520,
    location: { state: "Lagos State", lga: "Eti-Osa", area: "Victoria Island" },
    verified: { id: true, phone: true, certification: true },
    bio: "Award-winning photographer with a passion for capturing life's precious moments. Specializing in weddings and corporate events.",
    joinedDate: "2019-01-05",
    responseTime: "Within 3 hours",
    reviews: [],
  },
  // Domestic Services
  {
    id: "p8",
    name: "Fatima Abdullahi",
    businessName: "Sparkle Clean Services",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=400&fit=crop",
    designation: "Professional Cleaner",
    rank: "Professional",
    yearsOfExperience: 8,
    categoryId: "domestic",
    subCategoryId: "cleaning",
    services: ["Deep Cleaning", "Regular Cleaning", "Office Cleaning", "Post-Construction Cleaning", "Move-In/Move-Out"],
    pricingModel: "fixed",
    priceRange: { min: 15000, max: 80000 },
    availability: "online",
    rating: 4.8,
    reviewCount: 267,
    completedJobs: 480,
    location: { state: "FCT Abuja", lga: "Gwarinpa", area: "Gwarinpa 1" },
    verified: { id: true, phone: true, certification: false },
    bio: "Leading a team of professional cleaners. We make your space sparkle! Eco-friendly products and attention to detail guaranteed.",
    joinedDate: "2020-04-12",
    responseTime: "Within 1 hour",
    reviews: [],
  },
  // Health & Wellness
  {
    id: "p9",
    name: "Ngozi Okonkwo",
    businessName: "FitLife Training",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=400&fit=crop",
    designation: "Certified Fitness Trainer",
    rank: "Professional",
    yearsOfExperience: 6,
    categoryId: "health",
    subCategoryId: "fitness",
    services: ["Personal Training", "Group Classes", "Weight Loss Programs", "Strength Training", "Online Coaching"],
    pricingModel: "hourly",
    priceRange: { min: 5000, max: 25000 },
    availability: "online",
    rating: 4.9,
    reviewCount: 189,
    completedJobs: 320,
    location: { state: "Lagos State", lga: "Eti-Osa", area: "Lekki Phase 1" },
    verified: { id: true, phone: true, certification: true },
    bio: "Certified personal trainer helping you achieve your fitness goals. Personalized programs for all fitness levels. Let's get fit together!",
    joinedDate: "2021-06-01",
    responseTime: "Within 30 minutes",
    reviews: [],
  },
  // Education
  {
    id: "p10",
    name: "Dr. Ahmed Yusuf",
    businessName: "Excel Tutoring",
    avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=800&h=400&fit=crop",
    designation: "Academic Tutor",
    rank: "Master",
    yearsOfExperience: 15,
    categoryId: "education",
    subCategoryId: "tutoring",
    services: ["Mathematics", "Physics", "Chemistry", "JAMB Preparation", "WAEC Preparation"],
    pricingModel: "hourly",
    priceRange: { min: 3000, max: 10000 },
    availability: "online",
    rating: 4.9,
    reviewCount: 456,
    completedJobs: 890,
    location: { state: "Kano State", lga: "Kano Municipal", area: "Sabon Gari" },
    verified: { id: true, phone: true, certification: true },
    bio: "PhD holder with 15 years teaching experience. Specializing in STEM subjects with proven track record of helping students excel.",
    joinedDate: "2018-08-10",
    responseTime: "Within 2 hours",
    reviews: [],
  },
  // Logistics
  {
    id: "p11",
    name: "Samuel Adekunle",
    businessName: "SwiftMove Logistics",
    avatar: "https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=800&h=400&fit=crop",
    designation: "Logistics Coordinator",
    rank: "Professional",
    yearsOfExperience: 10,
    categoryId: "logistics",
    subCategoryId: "moving",
    services: ["Home Moving", "Office Relocation", "Packing Services", "Storage Solutions", "Interstate Moving"],
    pricingModel: "inspection",
    priceRange: { min: 30000, max: 300000 },
    availability: "online",
    rating: 4.7,
    reviewCount: 234,
    completedJobs: 450,
    location: { state: "Lagos State", lga: "Alimosho", area: "Ikotun" },
    verified: { id: true, phone: true, certification: false },
    bio: "Making your move stress-free! Professional moving services with careful handling and on-time delivery guaranteed.",
    joinedDate: "2019-11-20",
    responseTime: "Within 1 hour",
    reviews: [],
  },
  // Agriculture
  {
    id: "p12",
    name: "Musa Aliyu",
    businessName: "GreenFields Agro",
    avatar: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=150&h=150&fit=crop&crop=face",
    coverImage: "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800&h=400&fit=crop",
    designation: "Agricultural Consultant",
    rank: "Professional",
    yearsOfExperience: 12,
    categoryId: "agriculture",
    subCategoryId: "farming",
    services: ["Crop Farming Advice", "Soil Analysis", "Farm Planning", "Irrigation Setup", "Pest Management"],
    pricingModel: "fixed",
    priceRange: { min: 25000, max: 150000 },
    availability: "online",
    rating: 4.8,
    reviewCount: 98,
    completedJobs: 180,
    location: { state: "Oyo State", lga: "Ibadan North", area: "Bodija" },
    verified: { id: true, phone: true, certification: true },
    bio: "Helping farmers maximize yields with modern agricultural practices. Expert in crop cultivation and sustainable farming methods.",
    joinedDate: "2020-02-15",
    responseTime: "Within 3 hours",
    reviews: [],
  },
];

export const featuredProviders = sampleProviders.filter(p => p.rating >= 4.8 && p.rank === "Master" || p.rank === "Professional");
export const topRatedProviders = [...sampleProviders].sort((a, b) => b.rating - a.rating).slice(0, 6);
